import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "../services/httpClient";
import { toast } from "react-toastify";
import {
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp,
  FaEllipsisV,
} from "react-icons/fa";
import { MathJax, MathJaxContext } from "better-react-mathjax";

const mathJaxConfig = {
  tex: { inlineMath: [["$", "$"]] },
};

const boards = ["CBSE", "ICSE", "IB", "IGCSE", "Cambridge"];
const grades = Array.from({ length: 12 }, (_, i) => i + 1);
const levels = ["Beginner", "Developing", "Proficient", "Advanced", "Mastery"];
const types = ["MCQ", "VSA", "SA", "Speaking", "Video", "Ordering"];

// Color mapping for labels
const getBoardColor = (board) => {
  const colors = {
    CBSE: "bg-blue-100 text-blue-800",
    ICSE: "bg-green-100 text-green-800",
    IB: "bg-purple-100 text-purple-800",
    IGCSE: "bg-orange-100 text-orange-800",
    Cambridge: "bg-red-100 text-red-800",
  };
  return colors[board] || "bg-gray-100 text-gray-800";
};

const getGradeColor = (grade) => {
  const num = parseInt(grade);
  if (num <= 5) return "bg-yellow-100 text-yellow-800";
  if (num <= 8) return "bg-blue-100 text-blue-800";
  return "bg-indigo-100 text-indigo-800";
};

const getSubjectColor = (subject) => {
  const colors = {
    Mathematics: "bg-blue-100 text-blue-800",
    Physics: "bg-red-100 text-red-800",
    Chemistry: "bg-green-100 text-green-800",
    Biology: "bg-emerald-100 text-emerald-800",
    English: "bg-purple-100 text-purple-800",
  };
  return colors[subject] || "bg-gray-100 text-gray-800";
};

const getLevelColor = (level) => {
  // All levels are yellow
  return "bg-yellow-100 text-yellow-800";
};

const getTypeColor = (type) => {
  // All types are green
  return "bg-green-100 text-green-800";
};

const AdminQuestions = () => {
  const [questionsList, setQuestionsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [pageInput, setPageInput] = useState("");

  // Board and Grade filters (first row)
  const [board, setBoard] = useState("");
  const [grade, setGrade] = useState("");

  // Syllabus data
  const [syllabusData, setSyllabusData] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [loadingSyllabus, setLoadingSyllabus] = useState(false);

  // Other filters
  const [subject, setSubject] = useState("");
  const [chapter, setChapter] = useState("");
  const [level, setLevel] = useState("");
  const [type, setType] = useState("");
  const [numOptions, setNumOptions] = useState("");
  const [correctIndex, setCorrectIndex] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filters collapse state
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const hasInitialFetch = useRef(false);
  const prevPageRef = useRef(1);
  const prevFiltersRef = useRef({});

  // Fetch syllabus when board and grade are selected
  useEffect(() => {
    const fetchSyllabus = async () => {
      if (!board || !grade) {
        setSyllabusData([]);
        setSubjects([]);
        setChapters([]);
        setSubject("");
        setChapter("");
        return;
      }

      setLoadingSyllabus(true);
      try {
        const response = await axios.post(
          `/api/v2/syllabus/public`,
          {
            board: board,
            grade: grade.toString(),
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const data = response.data || [];
        setSyllabusData(data);

        // Extract unique subjects
        const uniqueSubjects = [...new Set(data.map((item) => item.subject))];
        setSubjects(uniqueSubjects);

        // Reset subject and chapter when board/grade changes
        setSubject("");
        setChapter("");
        setChapters([]);
      } catch (error) {
        console.error("Error fetching syllabus:", error);
        toast.error("Failed to load syllabus data");
        setSyllabusData([]);
        setSubjects([]);
        setChapters([]);
      } finally {
        setLoadingSyllabus(false);
      }
    };

    fetchSyllabus();
  }, [board, grade]);

  // Update chapters when subject changes
  useEffect(() => {
    if (subject && syllabusData.length > 0) {
      const subjectData = syllabusData.find((item) => item.subject === subject);
      if (subjectData && subjectData.chapters) {
        const chaptersData = subjectData.chapters.map((ch) => ({
          name: ch.name,
        }));
        setChapters(chaptersData);
        setChapter(""); // Reset chapter selection
      } else {
        setChapters([]);
        setChapter("");
      }
    } else {
      setChapters([]);
      setChapter("");
    }
  }, [subject, syllabusData]);

  // Fetch questions list
  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: pageSize.toString(),
      });

      if (board) params.append("board", board);
      if (grade) params.append("grade", grade);
      if (subject) params.append("subject", subject);
      if (chapter) params.append("chapter", chapter);
      if (level) params.append("level", level);
      if (type) params.append("type", type);
      if (numOptions) params.append("num_options", numOptions);
      if (correctIndex) params.append("correct_index", correctIndex);

      if (startDate) {
        const date = new Date(startDate);
        date.setUTCHours(0, 0, 0, 0);
        params.append("start_date", date.toISOString());
      }

      if (endDate) {
        const date = new Date(endDate);
        date.setUTCHours(23, 59, 59, 999);
        params.append("end_date", date.toISOString());
      }

      const response = await axios.get(
        `/v2/question-pool?${params.toString()}`
      );

      if (response.data) {
        const items = Array.isArray(response.data.items)
          ? response.data.items
          : Array.isArray(response.data)
          ? response.data
          : [];

        setQuestionsList(items);

        // Calculate total count
        const total =
          response.data.total ||
          response.data.total_count ||
          response.data.totalCount ||
          items.length;
        setTotalCount(total);

        // Calculate total pages from total count and page size
        const calculatedTotalPages =
          total > 0 ? Math.ceil(total / pageSize) : 1;
        setTotalPages(
          response.data.total_pages ||
            response.data.totalPages ||
            calculatedTotalPages
        );
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error("Failed to load questions. Please try again.");
      setQuestionsList([]);
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    board,
    grade,
    subject,
    chapter,
    level,
    type,
    numOptions,
    correctIndex,
    startDate,
    endDate,
  ]);

  // Initial fetch on mount
  useEffect(() => {
    if (!hasInitialFetch.current) {
      hasInitialFetch.current = true;
      prevPageRef.current = currentPage;
      prevFiltersRef.current = {
        board,
        grade,
        subject,
        chapter,
        level,
        type,
        numOptions,
        correctIndex,
        startDate,
        endDate,
      };
      fetchQuestions();
    }
  }, []);

  // Clear page input when page changes
  useEffect(() => {
    setPageInput("");
  }, [currentPage]);

  // Fetch on page change only (not on filter change)
  useEffect(() => {
    if (!hasInitialFetch.current) {
      return;
    }

    const pageChanged = prevPageRef.current !== currentPage;

    if (pageChanged) {
      prevPageRef.current = currentPage;
      fetchQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Handle Apply Filters button click
  const handleApplyFilters = () => {
    setCurrentPage(1); // Reset to first page when applying filters
    setPageInput("");
    prevPageRef.current = 1;
    prevFiltersRef.current = {
      board,
      grade,
      subject,
      chapter,
      level,
      type,
      numOptions,
      correctIndex,
      startDate,
      endDate,
    };
    fetchQuestions();
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  // Truncate text for table display (multi-line)
  const truncateText = (text, maxLength = 200) => {
    if (!text) return "N/A";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  // Handle disable question
  const handleDisableQuestion = async (question, e) => {
    e.stopPropagation(); // Prevent row click

    const questionId = question.id || question.question_id;
    if (!questionId) {
      toast.error("Question ID not found");
      return;
    }

    try {
      const response = await axios.patch(
        `/v2/question-pool/${questionId}/disable`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200 || response.status === 204) {
        toast.success("Question disabled successfully");
        // Refresh the list
        fetchQuestions();
      }
    } catch (error) {
      console.error("Error disabling question:", error);
      toast.error(
        error.response?.data?.message || "Failed to disable question"
      );
    }
  };

  // Handle row click
  const handleRowClick = (question) => {
    setSelectedQuestion(question);
    setShowDetailPanel(true);
  };

  // Clear filters
  const handleClearFilters = () => {
    setBoard("");
    setGrade("");
    setSubject("");
    setChapter("");
    setLevel("");
    setType("");
    setNumOptions("");
    setCorrectIndex("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  // Extract options from question
  const extractOptions = (question) => {
    if (Array.isArray(question?.options))
      return question.options.map((opt) =>
        typeof opt === "string"
          ? opt
          : opt?.option_text || opt?.text || opt?.label || opt?.value || ""
      );
    if (Array.isArray(question?.choices))
      return question.choices.map((choice) =>
        typeof choice === "string"
          ? choice
          : choice?.option_text || choice?.text || choice?.label || ""
      );
    return [];
  };

  const options = selectedQuestion ? extractOptions(selectedQuestion) : [];
  const correctIndexValue =
    selectedQuestion?.correct_option_index ??
    selectedQuestion?.correctAnswer ??
    selectedQuestion?.correct_index ??
    null;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Admin Questions</h1>
          <div className="text-lg font-semibold text-gray-700">
            Total Questions:{" "}
            <span className="text-primary">{totalCount.toLocaleString()}</span>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 mb-6 overflow-hidden">
          {/* Filter Header with Collapse Button */}
          <div
            className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 cursor-pointer hover:from-gray-100 hover:to-gray-50 transition-colors"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-800">Filters</h2>
              {(board ||
                grade ||
                subject ||
                chapter ||
                level ||
                type ||
                numOptions ||
                correctIndex ||
                startDate ||
                endDate) && (
                <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  Active
                </span>
              )}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setFiltersExpanded(!filtersExpanded);
              }}
              className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-lg hover:bg-gray-200"
            >
              {filtersExpanded ? (
                <FaChevronUp className="w-5 h-5" />
              ) : (
                <FaChevronDown className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Filter Content - Collapsible */}
          {filtersExpanded && (
            <div className="p-6 bg-gray-50/50">
              {/* Board and Grade Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 pb-4 border-b border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Board
                  </label>
                  <select
                    value={board}
                    onChange={(e) => setBoard(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <option value="">Select Board</option>
                    {boards.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Grade
                  </label>
                  <select
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <option value="">Select Grade</option>
                    {grades.map((g) => (
                      <option key={g} value={g}>
                        Grade {g}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Other Filters Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <select
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    disabled={!board || !grade || loadingSyllabus}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {loadingSyllabus
                        ? "Loading..."
                        : !board || !grade
                        ? "Select Board & Grade first"
                        : "Select Subject"}
                    </option>
                    {subjects.map((sub) => (
                      <option key={sub} value={sub}>
                        {sub}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Chapter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chapter
                  </label>
                  <select
                    value={chapter}
                    onChange={(e) => setChapter(e.target.value)}
                    disabled={!subject || chapters.length === 0}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {!subject
                        ? "Select Subject first"
                        : chapters.length === 0
                        ? "No chapters available"
                        : "Select Chapter"}
                    </option>
                    {chapters.map((ch) => (
                      <option key={ch.name} value={ch.name}>
                        {ch.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Level
                  </label>
                  <select
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <option value="">All Levels</option>
                    {levels.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                  </label>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <option value="">All Types</option>
                    {types.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Number of Options */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Options
                  </label>
                  <input
                    type="number"
                    value={numOptions}
                    onChange={(e) => setNumOptions(e.target.value)}
                    min="1"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
                    placeholder="e.g., 4"
                  />
                </div>

                {/* Correct Index */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Correct Index
                  </label>
                  <input
                    type="number"
                    value={correctIndex}
                    onChange={(e) => setCorrectIndex(e.target.value)}
                    min="0"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
                    placeholder="e.g., 1"
                  />
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <div className="relative">
                    <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <div className="relative">
                    <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                </div>
              </div>

              {/* Apply and Clear Filters Buttons */}
              <div className="mt-6 pt-4 border-t border-gray-200 flex items-center gap-3">
                <button
                  onClick={handleApplyFilters}
                  className="px-6 py-2.5 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 transition-all shadow-sm"
                >
                  Apply Filters
                </button>
                {(board ||
                  grade ||
                  subject ||
                  chapter ||
                  level ||
                  type ||
                  numOptions ||
                  correctIndex ||
                  startDate ||
                  endDate) && (
                  <button
                    onClick={handleClearFilters}
                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-gray-500">Loading questions...</p>
            </div>
          ) : questionsList.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500 text-lg">No questions found</p>
              <p className="text-gray-400 text-sm mt-2">
                Try adjusting your filters
              </p>
            </div>
          ) : (
            <MathJaxContext config={mathJaxConfig}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-2/5">
                        Question Text
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Board
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Grade
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Subject
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Chapter
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Level
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        <div className="leading-tight">
                          Option
                          <br />
                          count
                        </div>
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {questionsList.map((question, index) => (
                      <tr
                        key={question.id || question.question_id || index}
                        onClick={() => handleRowClick(question)}
                        className="hover:bg-blue-50/50 cursor-pointer transition-colors group"
                      >
                        <td className="px-4 py-3">
                          <MathJax dynamic>
                            <div
                              className="text-sm text-gray-900 line-clamp-3"
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                maxWidth: "100%",
                              }}
                              title={
                                question.question_text ||
                                question.questionText ||
                                question.question ||
                                ""
                              }
                            >
                              {question.question_text ||
                                question.questionText ||
                                question.question ||
                                "N/A"}
                            </div>
                          </MathJax>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          {question.board && (
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${getBoardColor(
                                question.board
                              )}`}
                            >
                              {question.board}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          {question.grade && (
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${getGradeColor(
                                question.grade
                              )}`}
                            >
                              {question.grade}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {question.subject && (
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getSubjectColor(
                                question.subject
                              )} truncate max-w-[120px]`}
                              title={question.subject}
                            >
                              {question.subject}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {question.chapter && (
                            <span
                              className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 truncate max-w-[120px]"
                              title={question.chapter}
                            >
                              {question.chapter}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          {question.level && (
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${getLevelColor(
                                question.level
                              )}`}
                            >
                              {question.level}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          {question.type && (
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${getTypeColor(
                                question.type
                              )}`}
                            >
                              {question.type}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-center">
                          <span className="text-xs text-gray-600 font-medium">
                            {question.num_options ||
                              (Array.isArray(question.options)
                                ? question.options.length
                                : Array.isArray(question.choices)
                                ? question.choices.length
                                : "—")}
                          </span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="text-xs text-gray-500">
                            {formatDate(
                              question.created_at || question.createdAt
                            )}
                          </span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-center relative">
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuIndex(
                                  openMenuIndex === index ? null : index
                                );
                              }}
                              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all"
                              title="More options"
                            >
                              <FaEllipsisV className="w-4 h-4" />
                            </button>

                            {/* Dropdown Menu */}
                            {openMenuIndex === index && (
                              <>
                                {/* Backdrop to close menu */}
                                <div
                                  className="fixed inset-0 z-10"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuIndex(null);
                                  }}
                                />
                                {/* Menu */}
                                <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDisableQuestion(question, e);
                                      setOpenMenuIndex(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                                  >
                                    <span>Disable Question</span>
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Showing page {currentPage} of {totalPages} • Total:{" "}
                  <span className="font-semibold text-primary">
                    {totalCount.toLocaleString()}
                  </span>{" "}
                  questions
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-center">
                  {/* First Page */}
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="First page"
                  >
                    First
                  </button>

                  {/* Previous */}
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FaChevronLeft className="inline mr-1" />
                    Prev
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {(() => {
                      const pages = [];
                      const showPages = 5; // Show 5 page numbers around current
                      let startPage = Math.max(
                        1,
                        currentPage - Math.floor(showPages / 2)
                      );
                      let endPage = Math.min(
                        totalPages,
                        startPage + showPages - 1
                      );

                      // Adjust start if we're near the end
                      if (endPage - startPage < showPages - 1) {
                        startPage = Math.max(1, endPage - showPages + 1);
                      }

                      // First page and ellipsis
                      if (startPage > 1) {
                        pages.push(
                          <button
                            key={1}
                            onClick={() => setCurrentPage(1)}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                          >
                            1
                          </button>
                        );
                        if (startPage > 2) {
                          pages.push(
                            <span
                              key="ellipsis1"
                              className="px-2 text-gray-500"
                            >
                              ...
                            </span>
                          );
                        }
                      }

                      // Page number buttons
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => setCurrentPage(i)}
                            className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                              i === currentPage
                                ? "bg-primary text-white border-primary"
                                : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }

                      // Last page and ellipsis
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(
                            <span
                              key="ellipsis2"
                              className="px-2 text-gray-500"
                            >
                              ...
                            </span>
                          );
                        }
                        pages.push(
                          <button
                            key={totalPages}
                            onClick={() => setCurrentPage(totalPages)}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                          >
                            {totalPages}
                          </button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  {/* Next */}
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <FaChevronRight className="inline ml-1" />
                  </button>

                  {/* Last Page */}
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Last page"
                  >
                    Last
                  </button>

                  {/* Go to Page Input */}
                  <div className="flex items-center gap-2 ml-2 pl-2 border-l border-gray-300">
                    <span className="text-sm text-gray-600">Go to:</span>
                    <input
                      type="number"
                      min="1"
                      max={totalPages}
                      value={pageInput}
                      onChange={(e) => setPageInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          const page = parseInt(pageInput);
                          if (page >= 1 && page <= totalPages) {
                            setCurrentPage(page);
                            setPageInput("");
                          } else {
                            toast.error(
                              `Please enter a page number between 1 and ${totalPages}`
                            );
                            setPageInput("");
                          }
                        }
                      }}
                      placeholder="Page"
                      className="w-16 px-2 py-1.5 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                    <button
                      onClick={() => {
                        const page = parseInt(pageInput);
                        if (page >= 1 && page <= totalPages) {
                          setCurrentPage(page);
                          setPageInput("");
                        } else {
                          toast.error(
                            `Please enter a page number between 1 and ${totalPages}`
                          );
                          setPageInput("");
                        }
                      }}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      Go
                    </button>
                  </div>
                </div>
              </div>
            </MathJaxContext>
          )}
        </div>
      </div>

      {/* Detail Panel (Right Side) */}
      {showDetailPanel && selectedQuestion && (
        <div className="fixed inset-0 z-50 flex">
          {/* Overlay - Light blueish with transparency */}
          <div
            className="flex-1 bg-primary/20 backdrop-blur-sm"
            onClick={() => setShowDetailPanel(false)}
          />

          {/* Panel */}
          <div className="bg-white w-full md:w-1/2 lg:w-2/5 h-full flex flex-col shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b bg-gray-50">
              <h2 className="text-xl font-semibold text-gray-800">
                Question Details
              </h2>
              <button
                onClick={() => setShowDetailPanel(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors p-1 hover:bg-gray-100 rounded-full"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <MathJaxContext config={mathJaxConfig}>
                <div className="space-y-6">
                  {/* Question Text */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Question
                    </label>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <MathJax dynamic>
                        <div className="text-gray-900 whitespace-pre-wrap">
                          {selectedQuestion.question_text ||
                            selectedQuestion.questionText ||
                            selectedQuestion.question ||
                            "N/A"}
                        </div>
                      </MathJax>
                    </div>
                  </div>

                  {/* Options */}
                  {options.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Options
                      </label>
                      <div className="space-y-3">
                        {options.map((option, index) => {
                          const isCorrect =
                            typeof correctIndexValue === "number" &&
                            index === correctIndexValue;
                          return (
                            <div
                              key={index}
                              className={`p-4 rounded-lg border-2 ${
                                isCorrect
                                  ? "bg-green-50 border-green-500"
                                  : "bg-gray-50 border-gray-200"
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <div
                                  className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-semibold ${
                                    isCorrect
                                      ? "bg-green-500 text-white"
                                      : "bg-gray-300 text-gray-700"
                                  }`}
                                >
                                  {String.fromCharCode(65 + index)}
                                </div>
                                <div className="flex-1">
                                  <MathJax dynamic>
                                    <div className="text-gray-900 whitespace-pre-wrap">
                                      {option || ""}
                                    </div>
                                  </MathJax>
                                </div>
                                {isCorrect && (
                                  <span className="text-green-600 font-semibold text-sm">
                                    ✓ Correct
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Reasoning/Explanation */}
                  {(selectedQuestion.reasoning ||
                    selectedQuestion.explanation ||
                    selectedQuestion.resoning) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reasoning / Explanation
                      </label>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <MathJax dynamic>
                          <div className="text-gray-900 whitespace-pre-wrap">
                            {selectedQuestion.reasoning ||
                              selectedQuestion.explanation ||
                              selectedQuestion.resoning ||
                              "N/A"}
                          </div>
                        </MathJax>
                      </div>
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Board
                      </label>
                      <p className="text-gray-900">
                        {selectedQuestion.board || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Grade
                      </label>
                      <p className="text-gray-900">
                        {selectedQuestion.grade || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subject
                      </label>
                      <p className="text-gray-900">
                        {selectedQuestion.subject || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Chapter
                      </label>
                      <p className="text-gray-900">
                        {selectedQuestion.chapter || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Level
                      </label>
                      <p className="text-gray-900">
                        {selectedQuestion.level || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <p className="text-gray-900">
                        {selectedQuestion.type || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Created At
                      </label>
                      <p className="text-gray-900">
                        {formatDate(
                          selectedQuestion.created_at ||
                            selectedQuestion.createdAt
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </MathJaxContext>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuestions;
